import { applicationErrorMessage } from "../lib/applicationErrors.mjs";
import { useAuth } from "@/context/AuthContext";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { formatTuition } from "../lib/studyCatalogue";

export default function StudyWorkspace() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  return <StudyWorkspaceForm key={JSON.stringify([params.get("courseId"), params.get("mode"), user?.id, user?.tenant_id])} />;
}

function StudyWorkspaceForm() {
  const [searchParams] = useSearchParams();
  const courseId = searchParams.get("courseId") || "";
  const mode = searchParams.get("mode") === "enquiry" ? "enquiry" : "application";
  const [course, setCourse] = useState(null);
  const [shortlists, setShortlists] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [details, setDetails] = useState("");
  const [form, setForm] = useState({ fullName: "", email: "", phone: "", whatsapp: "" });
  const [biodata, setBiodata] = useState(null);
  const actionPending = useRef(false);
  const loadVersion = useRef(0);
  const [shortlisting, setShortlisting] = useState(false);
  const [messageError, setMessageError] = useState(false);
  const [attachmentApplication, setAttachmentApplication] = useState(null);

  const load = useCallback(async () => {
    if (!courseId) {
      setCourse(null); setError("Please select a course before starting an application.");
      setLoading(false); return;
    }
    const version = ++loadVersion.current;
    setLoading(true); setError("");
    try {
      const selected = await api.get(`/study/catalogue/${encodeURIComponent(courseId)}`);
      if (version !== loadVersion.current) return;
      setCourse(selected.data);
      {
        const [saved, submitted] = await Promise.all([
          api.get("/study/shortlists"), api.get("/study/applications/mine"),
        ]);
        if (version !== loadVersion.current) return;
        setShortlists(saved.data || []); setApplications(submitted.data || []);
      }
    } catch (e) {
      if (version !== loadVersion.current) return;
      setCourse(null);
      setError(e?.response?.status === 404 ? "This course is unavailable or is not published." : applicationErrorMessage(e, "Unable to load this course and your study history. Sign in and try again."));
    } finally { if (version === loadVersion.current) setLoading(false); }
  }, [courseId]);
  useEffect(() => { load(); }, [load]);

  const shortlist = async () => {
    if (actionPending.current) return;
    actionPending.current = true; setShortlisting(true); setMessage(""); setMessageError(false);
    try {
      await api.post("/study/shortlists", {
        provider_id: course.institution_id || course.institution_name,
        provider_name: course.institution_name, country: course.country || "Not specified",
        course_id: course.id, course_title: course.programme_name,
      });
      setShortlists((current) => [...current.filter((item) => item.course_id !== course.id), { course_id: course.id }]);
      setMessage("Course added to your shortlist.");
    } catch (e) { setMessageError(true); setMessage(applicationErrorMessage(e, "Unable to shortlist this course. Please try again.")); }
    finally { actionPending.current = false; setShortlisting(false); }
  };

  const attachBiodata = async (application) => {
    const body = new FormData();
    body.append("entity_type", "study_application"); body.append("entity_id", application.id);
    body.append("category", "candidate_biodata"); body.append("file", biodata);
    await api.post("/files", body);
    setAttachmentApplication(null);
  };

  const retryBiodata = async () => {
    if (actionPending.current || !attachmentApplication || !biodata) return;
    actionPending.current = true; setSubmitting(true); setMessageError(false);
    setMessage("Uploading biodata to your existing study application…");
    try {
      await attachBiodata(attachmentApplication);
      setMessage("Biodata uploaded to your existing study application.");
    } catch (e) {
      setMessageError(true);
      setMessage("Your enquiry/application is already received. " + applicationErrorMessage(e, "Biodata upload failed. Please retry."));
    } finally { actionPending.current = false; setSubmitting(false); }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (actionPending.current) return;
    setMessageError(false);
    if (!course || Object.values(form).some((value) => !value.trim()) || details.trim().length < 10) {
      setMessageError(true);
      setMessage("Please complete the candidate name, email, phone, WhatsApp number and details."); return;
    }
    if (applications.some((item) => item.course_id === course.id && item.is_deleted !== true)) {
      setMessageError(true);
      setMessage("You have already submitted an enquiry or application for this course."); return;
    }
    actionPending.current = true; setSubmitting(true); setMessage("");
    try {
      const { data: saved } = await api.post("/study/applications", {
        provider_id: course.institution_id || course.institution_name,
        provider_name: course.institution_name, course_id: course.id,
        course_title: course.programme_name, country: course.country || "Not specified",
        message: JSON.stringify({ mode, contact: form, details: details.trim() }),
      });
      setApplications((current) => [...current, saved]);
      if (biodata) {
        setAttachmentApplication(saved);
        try { await attachBiodata(saved); } catch (e) {
          setMessageError(true);
          setMessage("Your enquiry/application was received, but the biodata upload failed. Use Retry biodata upload below. " + applicationErrorMessage(e, "") + (saved.warning ? " " + saved.warning : ""));
          return;
        }
      }
      setMessage(saved.warning || (mode === "enquiry" ? "Course enquiry submitted for staff review." : "Study application submitted for staff review."));
      setMessageError(!!saved.warning);
    } catch (e) { setMessageError(true); setMessage(applicationErrorMessage(e, "Unable to submit this study request. Please try again.")); }
    finally { actionPending.current = false; setSubmitting(false); }
  };

  if (loading) return <main className="mx-auto max-w-4xl px-5 py-12"><p role="status">Loading your selected course…</p></main>;
  if (error || !course) return <main className="mx-auto max-w-4xl px-5 py-12" data-testid="workspace-course-error"><h1 className="text-2xl font-semibold">Select a course to continue</h1><p role="alert" className="mt-3 text-slate-600">{error}</p>{courseId && <button className="btn btn-ghost mt-6 mr-3" onClick={load}>Retry loading</button>}<Link className="btn btn-ghost mt-6 mr-3" to="/login">Sign in</Link><Link className="btn btn-primary mt-6" to="/study">Back to Study catalogue</Link></main>;

  const alreadySubmitted = applications.some((item) => item.course_id === course.id && item.is_deleted !== true);

  return <main className="mx-auto max-w-5xl px-5 py-10" data-testid="study-workspace-page">
    <Link className="text-sm text-blue-800 underline" to={`/study/course/${encodeURIComponent(course.id)}`}>← Return to course details</Link>
    <section className="card mt-6 p-6" data-testid={`selected-course-${course.id}`}>
      <p className="overline">Selected course</p><h1 className="mt-2 text-2xl font-semibold">{course.programme_name}</h1>
      <p className="mt-2 text-sm text-slate-600">{course.institution_name} · {[course.city, course.country].filter(Boolean).join(", ")}</p>
      <p className="mt-2 text-sm text-slate-600">{course.level || "Level not specified"} · {course.study_mode || "Study mode not specified"} · {formatTuition(course.tuition_fee_minor, course.currency)}</p>
      <button className="btn btn-ghost mt-4" type="button" onClick={shortlist} disabled={submitting || shortlisting || shortlists.some((item) => item.course_id === course.id)}>{shortlisting ? "Shortlisting…" : shortlists.some((item) => item.course_id === course.id) ? "Course shortlisted" : "Shortlist this course"}</button>
      {applications.filter((item) => item.course_id === course.id && item.is_deleted !== true).map((item) => <p key={item.id} className="mt-3 text-sm text-blue-900">Existing enquiry/application status: {item.status}</p>)}
    </section>
    <form className="card mt-6 p-6" onSubmit={submit} data-testid={`course-${mode}-form`}>
      <p className="overline">{mode === "enquiry" ? "Course enquiry" : "Course application"}</p>
      <h2 className="mt-2 text-xl font-semibold">{mode === "enquiry" ? "Enquire about" : "Apply for"} {course.programme_name}</h2>
      <fieldset disabled={submitting || shortlisting || alreadySubmitted}>
      <div className="mt-5 grid gap-4 sm:grid-cols-2">{[["fullName", "Candidate full name", "Full name"], ["email", "Email address", "candidate@example.com"], ["phone", "Contact number", "+44 7700 900000"], ["whatsapp", "WhatsApp number", "+44 7700 900000"]].map(([key, label, placeholder]) => <label key={key} className="text-sm font-medium">{label}<input className="field mt-1 w-full" type={key === "email" ? "email" : "text"} required placeholder={placeholder} value={form[key]} onChange={(e) => setForm((current) => ({ ...current, [key]: e.target.value }))} /></label>)}</div>
      <label className="mt-5 block text-sm font-medium">{mode === "enquiry" ? "Your question and study background" : "Application details and study goals"}<textarea className="field mt-1 min-h-32 w-full" required minLength={10} value={details} onChange={(e) => setDetails(e.target.value)} /></label>
      </fieldset>
      <label className="mt-5 block text-sm font-medium">Candidate biodata / CV <span className="font-normal text-slate-500">(optional)</span><input className="field mt-1 w-full" type="file" disabled={submitting || shortlisting || (alreadySubmitted && !attachmentApplication)} accept=".pdf,.docx,.jpg,.jpeg,.png" onChange={(e) => setBiodata(e.target.files?.[0] || null)} /></label>
      <p className="mt-4 text-xs text-slate-500">Do not enter passport numbers or sensitive identity information here. Optional files are malware-scanned before secure storage.</p>
      {message && <p className={messageError ? "mt-4 text-sm text-red-700" : "mt-4 text-sm text-blue-900"} role={messageError ? "alert" : "status"}>{message}</p>}
      {attachmentApplication && <button className="btn btn-ghost mt-5 mr-3" type="button" disabled={submitting || shortlisting} onClick={retryBiodata}>{submitting ? "Uploading…" : "Retry biodata upload"}</button>}
      <button className="btn btn-primary mt-5" type="submit" disabled={submitting || shortlisting || alreadySubmitted}>{alreadySubmitted ? "Already submitted" : submitting ? "Submitting…" : mode === "enquiry" ? "Submit course enquiry" : "Submit course application"}</button>
    </form>
  </main>;
}
